import get from "lodash/get";
import React, { useMemo } from "react";
import { PARAM_SEPARATOR } from "utils/editor/formMode";
import Fields from "components/ui/Fields";
import { useTranslation } from "react-i18next";
import Alert from "../Alert";
import {
  PackContainer,
  PackLabel,
  PackLogo,
  PackLogoIcon,
} from "../PackNameBlocks";

const FIELD_TYPE_TO_COMPONENT = {
  string: Fields.Input,
  radio: Fields.RadioGroup,
  bool: Fields.Checkbox,
};

export default function IntegrationPanel({
  inputParameters = [],
  dependencies,
  formData,
  onFieldChange,
}) {
  const { t } = useTranslation();
  const normalizedFields = useMemo(() => {
    return inputParameters.map((param) => {
      return {
        label: param.displayName,
        name: `${PARAM_SEPARATOR}${param.name}`,
        type: param.format,
        options: Object.keys(param.options).map((key) => ({
          value: key,
          label: param.options[key].label,
          description: param.options[key].description,
        })),
      };
    });
  }, [inputParameters]);

  const deps = useMemo(() => {
    const allDeps = dependencies || [];
    const eligibleDeps = normalizedFields
      .map((field, index) => {
        const value = get(formData, field.name);
        if (field.options) {
          return (
            inputParameters[index].options?.[value]?.dependencies || []
          ).map((dep) => dep.name);
        }

        return [];
      })
      .flat();

    return [...new Set(eligibleDeps)]
      .map((depName) => {
        return allDeps.find((depData) => depData.name === depName);
      })
      .filter(Boolean);
  }, [inputParameters, dependencies, formData, normalizedFields]);

  return (
    <div>
      {normalizedFields.map((field) => {
        const FieldComponent = FIELD_TYPE_TO_COMPONENT[field.type];
        return (
          <FieldComponent
            key={field.name}
            {...field}
            value={get(formData, field.name)}
            onChange={(value) => onFieldChange({ name: field.name, value })}
          />
        );
      })}
      {deps.length ? (
        <>
          <Fields.Label label={t("Dependencies")} required={true} />
          <Alert>
            {t(`
                The following packs are essential for
                this integration to function properly.
                If they are not already present, they
                will be automatically installed.
                Automatic installation will occur on
                new clusters or clusters being updated
                with this cluster profile.
            `)}

            {deps.map((dependency) => (
              <PackContainer key={dependency.guid}>
                <PackLogo>
                  <PackLogoIcon {...dependency} logoUrl={dependency.logoUrl} />
                </PackLogo>
                <PackLabel title={dependency.displayName}>
                  {dependency.displayName}
                </PackLabel>
              </PackContainer>
            ))}
          </Alert>
        </>
      ) : null}
    </div>
  );
}
